/* eslint-disable react/no-array-index-key */
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import EaBranding from '@propertypal/shared/src/resources/icons/products/ea-branding-icon.svg';
import EaListing from '@propertypal/shared/src/resources/icons/products/ea-listing-icon.svg';
import EaStationery from '@propertypal/shared/src/resources/icons/products/ea-stationery-icon.svg';
import DevBrandingMockup from '@propertypal/shared/src/resources/images/branding/dev-branding-mockup.png';
import DevLogoMockup from '@propertypal/shared/src/resources/images/branding/dev-logo-mockup.jpg';
import DevSignboardMockup from '@propertypal/shared/src/resources/images/branding/dev-signboard-mockup.jpg';
import DevStationeryMockup from '@propertypal/shared/src/resources/images/branding/dev-stationery-mockup.png';
import EaBrandingMockup from '@propertypal/shared/src/resources/images/branding/ea-branding-mockup.png';
import EaLogoMockup from '@propertypal/shared/src/resources/images/branding/ea-logo-mockup.jpg';
import EaSignboardMockup from '@propertypal/shared/src/resources/images/branding/ea-signboard-mockup.png';
import EaStationeryMockup from '@propertypal/shared/src/resources/images/branding/ea-stationery-mockup.png';
import React from 'react';
import ProductsPageHeader, { ProcutsHeaderIcon } from '../headers/products/ProductsPageHeader';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Box } from '../layout';
import { LinkText, Text } from '../typography';
import ProductScreenTabs from './ProductScreenTabs';
import {
  FeaturesContainer,
  FeaturesItem,
  FeaturesList,
  FeaturesSectionContainer,
  FeaturesSectionImage,
  FeaturesSectionImageContainer,
  FeaturesSectionSubTitle,
  FeaturesSectionTitle,
  FeaturesWrapper,
} from './ProductsFeatures.style';

const logoFeautures = [
  `Branding Guidelines`,
  <>
    <span>Logo in an EPS (Vector) format</span>{' '}
    <span style={{ display: 'block', fontStyle: 'italic' }}>
      (An EPS file, or Encapsulated PostScript file, is a versatile vector format of your logo which can be easily
      scaled without loss of quality)
    </span>
  </>,
  `Logo in all other relevant formats including JPG, PNG and SVG`,
  `Reversed Colour Logo`,
];

const stationeryFeautures = [
  `Business Card Design`,
  `Letterhead Design`,
  `Compliment Slip Design`,
  `Print ready files in PDF format`,
];

const signboardFeautures = [`Print ready files in PDF format`];

interface IconTextProps {
  scrollId: string;
}

const IconText: React.FC<IconTextProps> = ({ scrollId }) => (
  <LinkText
    as="span"
    display="flex"
    alignItems="center"
    gap={4}
    onClick={() => document.getElementById(scrollId)?.scrollIntoView({ behavior: 'smooth' })}
    fontSize={16}
    mt={5}
    data-testid={scrollId}
  >
    <span>Find out more</span>
    <FontAwesomeIcon icon={faChevronRight} size={14} />
  </LinkText>
);

interface BrandingFeaturesProps {
  dev?: boolean;
}

const icons: ProcutsHeaderIcon[] = [
  {
    SVG: EaBranding,
    heading: 'Bespoke Logo Design',
    text: <IconText scrollId="bespoke-logo-design" />,
    headingSize: 20,
    headingMt: 20,
  },
  {
    SVG: EaStationery,
    heading: 'Stationery Design',
    text: <IconText scrollId="stationery-design" />,
    headingSize: 20,
    headingMt: 20,
  },
  {
    SVG: EaListing,
    heading: 'Signboard Design',
    text: <IconText scrollId="signboard-design" />,
    headingSize: 20,
    headingMt: 20,
  },
];

const BrandingFeatures: React.FC<BrandingFeaturesProps> = ({ dev }) => {
  return (
    <>
      <ProductsPageHeader
        title="Branding"
        text="Full service solution for marketing your business"
        icon={<EaBranding />}
        headingProps={{ mb: 10 }}
        subHeadingProps={{ maxWidth: 670 }}
        icons={icons}
        pb={10}
      >
        <ProductScreenTabs
          aspectRatio="987 / 823"
          reverse
          mockup={{
            src: (dev ? DevBrandingMockup : EaBrandingMockup).src,
            alt: 'Branding',
          }}
          mockupWidth={500}
          maxW={1050}
          tabsMt={50}
          breakpoint={1050}
          button={{
            onClick: () => document.getElementById('contact-us')?.scrollIntoView({ behavior: 'smooth' }),
            text: 'Contact us',
            containerStyle: { maxWidth: '280px' },
          }}
          buttonMt={20}
          content={
            <Box maxWidth={440}>
              <Text lineHeight={25} align="left">
                Brand design is the cornerstone of any company&apos;s identity. We offer a range of digital services and
                products that can be perfectly tailored to your business. Whether you are just getting started or are an
                established {dev ? 'developer' : 'estate agent'}, we are the full service solution for marketing your
                business.
              </Text>
            </Box>
          }
          staticTabs
          mobPadding
        />
      </ProductsPageHeader>
      <FeaturesContainer id="bespoke-logo-design">
        <FeaturesWrapper>
          <FeaturesSectionContainer contentWidth={450} gap={90}>
            <FeaturesSectionImageContainer>
              <FeaturesSectionImage
                maxW={dev ? 430 : 600}
                src={(dev ? DevLogoMockup : EaLogoMockup).src}
                alt="Bespoke Logo Design"
              />
            </FeaturesSectionImageContainer>
            <Box maxWidth={450}>
              <FeaturesSectionTitle fontSize={22} mb={10}>
                Bespoke Logo Design
              </FeaturesSectionTitle>
              <FeaturesSectionSubTitle fontSize={16} mb={20} fontWeight="normal">
                Separate your business from the crowd
              </FeaturesSectionSubTitle>
              <Text mb={20} lineHeight={25}>
                We understand in a competitive market, your brand is everything. Our design team can provide your
                business with a logo design that perfectly reflects your company culture, ethos and values.
              </Text>
              <Text mb={30} lineHeight={25}>
                We will include various formats so that you can use your logo wherever you want in the correct format,
                whether printed or digital.
              </Text>
              <FeaturesSectionTitle fontSize={22} mb={20}>
                Logo Deliverables:
              </FeaturesSectionTitle>
              <FeaturesList>
                {logoFeautures.map((f, i) => (
                  <FeaturesItem key={i}>
                    <Text as="span">{f}</Text>
                  </FeaturesItem>
                ))}
              </FeaturesList>
            </Box>
          </FeaturesSectionContainer>
        </FeaturesWrapper>
      </FeaturesContainer>
      <FeaturesContainer id="stationery-design" altBg>
        <FeaturesWrapper maxW={1130}>
          <FeaturesSectionContainer contentWidth={440} gap={dev ? 50 : 10} reverse>
            <Box maxWidth={440}>
              <FeaturesSectionTitle fontSize={22} mb={15}>
                Stationery Design
              </FeaturesSectionTitle>
              <FeaturesSectionSubTitle fontSize={16} mb={20} fontWeight="normal">
                Provide consistent branding throughout your business communications
              </FeaturesSectionSubTitle>
              <Text mb={30} lineHeight={25}>
                The key to successful branding is its consistent application across all of your marketing communication
                with customers. Our team can provide you with professional stationery that puts your best foot forward
                throughout all your communications.
              </Text>
              <FeaturesSectionTitle fontSize={22} mb={20}>
                Stationery Deliverables:
              </FeaturesSectionTitle>
              <FeaturesList>
                {stationeryFeautures.map((f) => (
                  <FeaturesItem key={f}>
                    <Text as="span">{f}</Text>
                  </FeaturesItem>
                ))}
              </FeaturesList>
            </Box>
            <FeaturesSectionImageContainer>
              <FeaturesSectionImage
                src={(dev ? DevStationeryMockup : EaStationeryMockup).src}
                alt="Bespoke Stationery Design"
              />
            </FeaturesSectionImageContainer>
          </FeaturesSectionContainer>
        </FeaturesWrapper>
      </FeaturesContainer>
      <FeaturesContainer id="signboard-design">
        <FeaturesWrapper>
          <FeaturesSectionContainer contentWidth={450} gap={90}>
            <FeaturesSectionImageContainer>
              <FeaturesSectionImage
                src={(dev ? DevSignboardMockup : EaSignboardMockup).src}
                alt="Bespoke Signboard Design"
              />
            </FeaturesSectionImageContainer>
            <Box maxWidth={440}>
              <FeaturesSectionTitle fontSize={22} mb={15}>
                Signboard Design
              </FeaturesSectionTitle>
              <FeaturesSectionSubTitle fontSize={16} mb={20} fontWeight="normal">
                Professional board design
              </FeaturesSectionSubTitle>
              <Text mb={30} lineHeight={25}>
                Our team have years of experience creating eye catching, fresh and innovative designs, that are sure to
                make you stand out from the crowd.
              </Text>
              <FeaturesSectionTitle fontSize={22} mb={20}>
                Stationery Deliverables:
              </FeaturesSectionTitle>
              <FeaturesList>
                {signboardFeautures.map((f) => (
                  <FeaturesItem key={f}>
                    <Text as="span">{f}</Text>
                  </FeaturesItem>
                ))}
              </FeaturesList>
            </Box>
          </FeaturesSectionContainer>
        </FeaturesWrapper>
      </FeaturesContainer>
    </>
  );
};

export default BrandingFeatures;
